import { useState } from 'react';
import type { TicketDetail, TicketDetailArtifact, TicketVerificationView } from '@nightshift/ui-types';

import { EvidenceImage } from '../../components/evidence-image.tsx';
import { Markdown } from '../../components/markdown.tsx';
import { Empty } from '../../components/primitives.tsx';
import { api, ApiError } from '../../live/api.ts';
import { useStore } from '../../live/context.tsx';
import { cardExcerpt } from '../../lib/card-excerpt.ts';
import {
  artifactGroupSummary,
  groupArtifactsByKind,
  partitionArtifacts,
  type ArtifactGroup,
} from '../../lib/evidence-view.ts';
import {
  verificationHead,
  verificationRetryOffer,
  verificationRows,
  verificationStatusTone,
  verificationSummary,
} from '../../lib/verification-view.ts';
import { useTimeFormat } from '../../lib/display.ts';
import { bytes, clockStamp, observedTime } from '../../lib/format.ts';
import './evidence.css';

/**
 * Artifacts + verification_evidence. Staleness is marked only when a row exists
 * and is stale versus the current MR head (or the stored stale flag). A ticket may have no
 * evidence until its first verification run — SHA/CI are not invented.
 */
/**
 * Agent-uploaded screenshots live in the attempt's `<id>-evidence` folder and are the only
 * artifact files the orchestrator serves. Anything else stays a path.
 */
export function isEvidenceImage(filePath: string | null): boolean {
  if (!filePath) return false;
  const parts = filePath.split(/[\\/]/);
  const folder = parts[parts.length - 2] ?? '';
  return /-evidence$/.test(folder) && /\.(png|jpe?g|gif|webp)$/i.test(filePath);
}

function fileName(filePath: string | null): string {
  return filePath?.split(/[\\/]/).pop() ?? '';
}

function StaleMark({ row }: { row: TicketDetailArtifact }) {
  if (!row.stale) return null;
  return <span className="drawer-stale-mark">Stale{row.staleReason ? ` — ${row.staleReason}` : ''}</span>;
}

function Stamp({ at, now }: { at: number; now: number }) {
  const format = useTimeFormat();
  return (
    <time className="dim num" dateTime={new Date(at).toISOString()} title={clockStamp(at)}>
      {observedTime(at, now, format)}
    </time>
  );
}

/**
 * A document artifact — the planner's whole plan, refine's findings — collapsed to a label.
 *
 * Rendered as prose rather than as a mono one-liner: these refs are markdown bodies running to
 * kilobytes, and printed inline they arrive as a single wrapped paragraph with every newline
 * folded away. The ticket-detail payload carries only a preview, so expanding fetches the rest;
 * the fetch happens once and the body is kept for the life of the row.
 */
function DocumentArtifact({ row, now }: { row: TicketDetailArtifact; now: number }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(row.refTruncated ? null : row.ref);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (text !== null || loading) return;
    setLoading(true);
    try {
      const body = await api.artifactText(row.id);
      setText(body.text);
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setError('Your session expired. Sign in again to read this artifact.');
      } else {
        setError(caught instanceof Error ? caught.message : String(caught));
      }
    } finally {
      setLoading(false);
    }
  };

  const size = row.refChars === null ? null : bytes(row.refChars);
  // Flattened the way a board card flattens a problem: the raw source in a label is what made
  // this tab unreadable, and a peek at `# Plan ## Metadata` is that defect in miniature.
  const excerpt = cardExcerpt(row.ref ?? '');
  const peek = row.refTruncated && !excerpt.endsWith('…') ? `${excerpt}…` : excerpt;

  return (
    <li className={row.stale ? 'is-stale' : undefined}>
      <strong>{row.kind}</strong>
      {size ? <span className="dim"> {size}</span> : null}
      <StaleMark row={row} />
      <Stamp at={row.createdAt} now={now} />
      <button
        type="button"
        className="btn btn-sm drawer-evidence-toggle"
        aria-expanded={open}
        aria-controls={`artifact-doc-${row.id}`}
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next) void load();
        }}
      >
        {open ? 'Hide' : 'Read'}
      </button>
      {open ? null : <p className="drawer-evidence-preview dim">{peek.length > 0 ? peek : '—'}</p>}
      {open ? (
        <div className="drawer-evidence-doc" id={`artifact-doc-${row.id}`}>
          {error ? (
            <p className="drawer-evidence-doc-note">{error}</p>
          ) : text === null ? (
            <p className="drawer-evidence-doc-note dim">Loading…</p>
          ) : (
            <Markdown source={text} />
          )}
        </div>
      ) : null}
    </li>
  );
}

function PointerArtifact({ row, now }: { row: TicketDetailArtifact; now: number }) {
  const shot = isEvidenceImage(row.path);
  return (
    <li className={row.stale ? 'is-stale' : undefined}>
      <strong>{row.kind}</strong>
      <span className={shot ? 'dim' : 'mono dim'}>
        {' '}
        {shot ? (row.ref ?? fileName(row.path)) : (row.path ?? row.ref ?? '—')}
      </span>
      <StaleMark row={row} />
      <Stamp at={row.createdAt} now={now} />
      {shot ? (
        <a className="drawer-evidence-shot" href={`/api/artifacts/${row.id}/file`} target="_blank" rel="noreferrer">
          <EvidenceImage artifactId={row.id} alt={row.ref ?? row.kind} expiredAt={row.fileExpiredAt} lazy />
        </a>
      ) : null}
    </li>
  );
}

/** One artifact row, in whichever of the two shapes its ref calls for. */
function ArtifactRow({ row, now }: { row: TicketDetailArtifact; now: number }) {
  return row.refIsDocument && row.ref !== null ? (
    <DocumentArtifact row={row} now={now} />
  ) : (
    <PointerArtifact row={row} now={now} />
  );
}

/** `verification-proof` is safe as an id fragment already; anything exotic is made safe here. */
function groupDomId(prefix: string, kind: string): string {
  return `${prefix}-${kind.replace(/[^a-zA-Z0-9_-]+/g, '-')}`;
}

/**
 * One kind's artifacts.
 *
 * A small group is the list it has always been. A long one — the per-scenario CI harvests —
 * is one line saying what it is, how many there are and which head they belong to, because
 * eighty-two `capture` rows tell a reviewer nothing that "82 records at 79fe70e9" does not.
 * The rows are still there behind the toggle: a reviewer occasionally needs the individual
 * file, and a summary that could not be opened would just move the problem.
 */
function ArtifactKindGroup({
  group,
  now,
  idPrefix,
}: {
  group: ArtifactGroup;
  now: number;
  idPrefix: string;
}) {
  const [open, setOpen] = useState(false);

  if (!group.collapsed) {
    return (
      <ul className="drawer-evidence-list">
        {group.rows.map((row) => (
          <ArtifactRow key={row.id} row={row} now={now} />
        ))}
      </ul>
    );
  }

  const listId = groupDomId(idPrefix, group.kind);
  return (
    <div className="drawer-evidence-group">
      <button
        type="button"
        className="drawer-evidence-group-head"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(!open)}
      >
        <strong>{group.kind}</strong>
        <span className="dim">{artifactGroupSummary(group)}</span>
        <span className="drawer-evidence-group-toggle dim">{open ? 'Hide' : 'Show all'}</span>
      </button>
      {open ? (
        <ul className="drawer-evidence-list" id={listId}>
          {group.rows.map((row) => (
            <ArtifactRow key={row.id} row={row} now={now} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Exported for the drawer's own tests: the grouping is the part worth asserting. */
export function ArtifactGroups({
  rows,
  attempts,
  now,
  idPrefix,
}: {
  rows: TicketDetailArtifact[];
  attempts: TicketDetail['attempts'];
  now: number;
  idPrefix: string;
}) {
  return (
    <div className="drawer-evidence-groups">
      {groupArtifactsByKind(rows, attempts).map((group) => (
        <ArtifactKindGroup key={group.kind} group={group} now={now} idPrefix={idPrefix} />
      ))}
    </div>
  );
}

/**
 * Per-check verification for a project with a pinned producer plan.
 *
 * The status words come from the payload and are never recomputed here: they are the answer
 * the same core evaluator gives the merge guard, and a drawer that disagreed with the guard
 * would be worse than one that showed nothing. Retry verification sits beside the blocked
 * result it answers, and is drawn disabled with core's own refusal when a rerun of the
 * original job definition could not fix the fault.
 */
function VerificationChecks({
  view,
  ticketKey,
  expectedVersion,
  onReloaded,
}: {
  view: TicketVerificationView;
  ticketKey: string;
  expectedVersion: number;
  onReloaded?: () => void;
}) {
  const store = useStore();
  const [busy, setBusy] = useState(false);
  const head = verificationHead(view);
  const offer = verificationRetryOffer(view);

  const retry = async () => {
    setBusy(true);
    try {
      await api.retryVerification(ticketKey, expectedVersion);
      store.toast('good', 'Verification job retried');
      onReloaded?.();
    } catch (error) {
      store.toast('bad', error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h3 className="drawer-section-kicker">Required verification</h3>
      <p className="drawer-verify-summary dim">
        {verificationSummary(view)} at <span className="mono" title={head.full}>{head.short}</span>
      </p>
      <ul className="drawer-verify-list">
        {verificationRows(view).map((row) => (
          <li key={`${row.name}:${row.producer ?? ''}`} className={`is-${verificationStatusTone(row.status)}`}>
            <div className="drawer-verify-head">
              <strong>{row.name}</strong>
              <span className={`drawer-verify-status is-${verificationStatusTone(row.status)}`}>
                {row.status}
              </span>
              {row.producer ? <span className="dim">{row.producer}</span> : null}
              {row.jobUrl && row.jobId !== null ? (
                <a className="mono dim" href={row.jobUrl} target="_blank" rel="noreferrer">
                  job {row.jobId}
                </a>
              ) : row.jobId !== null ? (
                <span className="mono dim">job {row.jobId}</span>
              ) : null}
            </div>
            {row.cause ? <p className="drawer-verify-cause">{row.cause}</p> : null}
          </li>
        ))}
      </ul>
      {offer.visible ? (
        <div className="drawer-verify-actions">
          <button
            type="button"
            className="btn btn-accent btn-sm"
            disabled={busy || !offer.enabled}
            onClick={() => void retry()}
          >
            Retry verification
          </button>
          {offer.explanation ? (
            <p className="drawer-verify-cause" role="note">
              {offer.explanation}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function EvidenceTab({
  detail,
  now,
  onReloaded,
}: {
  detail: TicketDetail;
  now: number;
  onReloaded?: () => void;
}) {
  const format = useTimeFormat();
  const { live, stale } = partitionArtifacts(detail.artifacts);
  if (detail.artifacts.length === 0 && detail.evidence.length === 0 && !detail.verification) {
    return (
      <Empty
        title="No evidence yet."
        body="Artifacts and verification evidence appear here when an attempt records them. A ticket may have none until its first verification run — this is not a missing screenshot of a SHA that does not exist."
      />
    );
  }

  return (
    <div className="evidence-ws">
      {detail.verification ? (
        <VerificationChecks
          view={detail.verification}
          ticketKey={detail.ticket.ticketKey}
          expectedVersion={detail.ticket.version}
          {...(onReloaded ? { onReloaded } : {})}
        />
      ) : null}

      {detail.evidence.length > 0 ? (
        <section>
          <h3 className="drawer-section-kicker">Verification</h3>
          <ul className="drawer-evidence-list">
            {detail.evidence.map((row) => (
              <li key={row.id} className={row.stale ? 'is-stale' : undefined}>
                <strong>{row.type}</strong>
                <span className="dim"> {row.result}</span>
                {row.mrHeadSha ? <span className="mono dim"> {row.mrHeadSha.slice(0, 8)}</span> : null}
                {row.stale ? <span className="drawer-stale-mark">Stale{row.staleReason ? ` — ${row.staleReason}` : ''}</span> : null}
                <time className="dim num" dateTime={new Date(row.capturedAt).toISOString()}>
                  {observedTime(row.capturedAt, now, format)}
                </time>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {live.length > 0 ? (
        <section>
          <h3 className="drawer-section-kicker">Artifacts</h3>
          <ArtifactGroups rows={live} attempts={detail.attempts} now={now} idPrefix="artifact-live" />
        </section>
      ) : null}

      {/*
        Superseded work, kept reachable and kept out of the way. A reviewer sometimes needs to
        see what an earlier attempt produced, so these are collapsed rather than dropped — but
        on a three-attempt ticket they outnumber the live rows and must not be what the tab
        opens on.
      */}
      {stale.length > 0 ? (
        <details className="drawer-evidence-stale">
          <summary>
            Superseded by later attempts <span className="dim num">{stale.length}</span>
          </summary>
          <p className="dim drawer-evidence-stale-note">
            Captured against an earlier head or replaced by a newer artifact. Kept for history;
            nothing here counts towards the tab badge or the merge guard.
          </p>
          <ArtifactGroups rows={stale} attempts={detail.attempts} now={now} idPrefix="artifact-stale" />
        </details>
      ) : null}
    </div>
  );
}
